/**
 * <timeline-sticky> – gör bakgrunden mörkare ju längre man skrollat in i sektionen
 * och tonar ut introtexten. Overlayn går från data-overlay-start till
 * data-overlay-end över data-darken-distance procent av skärmhöjden, räknat från
 * det att sektionens överkant nått toppen av fönstret (då bakgrunden fastnar).
 *
 * Scroll/resize lyssnas bara på medan sektionen syns (IntersectionObserver), och
 * "reducera rörelse" följs även om inställningen ändras medan sidan är öppen.
 */
class TimelineSticky extends HTMLElement {
  #start = 0.4;
  #end = 0.4;
  #distance = 1;
  #fadeIntro = false;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #abort = null;
  #scrollAbort = null;
  #observer = null;
  #raf = 0;

  connectedCallback() {
    this.#start = parseFloat(this.dataset.overlayStart || '0.4');
    this.#end = parseFloat(this.dataset.overlayEnd || this.dataset.overlayStart || '0.4');
    this.#distance = parseFloat(this.dataset.darkenDistance || '100') / 100;
    this.#fadeIntro = this.dataset.fadeIntro === 'true';

    this.#abort = new AbortController();
    this.#reducedMotion.addEventListener('change', this.#requestUpdate, { signal: this.#abort.signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) this.#toggleScroll(entry.isIntersecting);
    });
    this.#observer.observe(this);

    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#scrollAbort?.abort();
    this.#scrollAbort = null;
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  #requestUpdate = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  };

  #toggleScroll(visible) {
    if (visible === Boolean(this.#scrollAbort)) return;

    if (!visible) {
      this.#scrollAbort.abort();
      this.#scrollAbort = null;
      // Sista läget när sektionen lämnar bild (t.ex. helt mörk efter snabb skroll)
      this.#update();
      return;
    }

    this.#scrollAbort = new AbortController();
    const { signal } = this.#scrollAbort;
    window.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#requestUpdate();
  }

  #update() {
    if (this.#reducedMotion.matches) {
      this.style.setProperty('--tl-overlay', String(this.#start));
      this.style.setProperty('--tl-intro-opacity', '1');
      return;
    }

    const scrolled = -this.getBoundingClientRect().top; // px in i sektionen
    const range = Math.max(window.innerHeight * this.#distance, 1);
    const progress = Math.min(Math.max(scrolled / range, 0), 1);
    const overlay = this.#start + (this.#end - this.#start) * progress;

    this.style.setProperty('--tl-overlay', overlay.toFixed(3));
    this.style.setProperty('--tl-intro-opacity', this.#fadeIntro ? (1 - progress).toFixed(3) : '1');
  }
}

if (!customElements.get('timeline-sticky')) {
  customElements.define('timeline-sticky', TimelineSticky);
}
